import React, { useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, MenuItem, Stack, Tab, Tabs, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, LineChart, Line, Legend } from "recharts";
import { Download } from "lucide-react";
import { CardHeader, GlassCard, T, TYPE, CHART, SERIES, ChartTooltip, labelSx } from "../../user/complaints/shared";
import { pm, errorMessage, saveBlob } from "../api";
import { usePm } from "../PmContext";
import { Progress, has } from "../ui";
import { fieldSx } from "../inputs";
import { Burndown, Velocity } from "./Sprints";

const REPORTS = [
  ["issues", "Issues"], ["sprint", "Sprint report"], ["burndown", "Burndown"], ["velocity", "Velocity"], ["release", "Releases"],
  ["workload", "Workload"], ["time", "Time tracking"], ["bugs", "Bugs"], ["completion", "Completion"],
];

const SimpleTable = ({ rows = [], cols }) => (
  <Box sx={{ overflowX: "auto" }}>
    <Table size="small">
      <TableHead><TableRow>{cols.map(([k, l]) => <TableCell key={k} sx={labelSx}>{l}</TableCell>)}</TableRow></TableHead>
      <TableBody>
        {!rows.length && <TableRow><TableCell colSpan={cols.length} sx={{ color: T.inkMuted, textAlign: "center" }}>No data</TableCell></TableRow>}
        {rows.map((r, i) => <TableRow key={i}>{cols.map(([k, , fmt]) => <TableCell key={k} sx={{ fontSize: TYPE.small }}>{fmt ? fmt(r[k], r) : r[k] ?? "—"}</TableCell>)}</TableRow>)}
      </TableBody>
    </Table>
  </Box>
);

const Bars = ({ data, x, bars, layout = "horizontal", height = 260 }) => (
  <Box sx={{ height, p: 2 }}>
    <ResponsiveContainer>
      <BarChart data={data} layout={layout}>
        <CartesianGrid stroke={CHART.grid} vertical={layout !== "horizontal"} horizontal={layout === "horizontal"} />
        {layout === "horizontal" ? <XAxis dataKey={x} tick={CHART.tick} {...CHART.axisProps} /> : <XAxis type="number" tick={CHART.tick} {...CHART.axisProps} />}
        {layout === "horizontal" ? <YAxis tick={CHART.tick} {...CHART.axisProps} width={34} /> : <YAxis type="category" dataKey={x} tick={CHART.tick} {...CHART.axisProps} width={120} />}
        <RTooltip content={<ChartTooltip />} />
        {bars.length > 1 && <Legend />}
        {bars.map(([k, l], i) => <Bar key={k} dataKey={k} name={l} fill={SERIES[i]} radius={layout === "horizontal" ? [4, 4, 0, 0] : [0, 4, 4, 0]} />)}
      </BarChart>
    </ResponsiveContainer>
  </Box>
);

export const ReportView = ({ name, params, project }) => {
  const ctx = usePm();
  // Results are tagged with the request they answer: after switching report or
  // filters, older data (or a slow earlier response) is never rendered with the
  // new report's layout.
  const reqKey = `${name}:${JSON.stringify(params)}`;
  const [result, setResult] = useState({ key: null, data: null, error: "" });
  useEffect(() => {
    if (name === "sprint" && !params.sprintId) return;
    let alive = true;
    pm.report(name, params)
      .then((d) => alive && setResult({ key: reqKey, data: d, error: "" }))
      .catch((err) => alive && setResult({ key: reqKey, data: null, error: errorMessage(err) }));
    return () => {
      alive = false;
    };
  }, [reqKey]); // eslint-disable-line react-hooks/exhaustive-deps
  if (name === "sprint" && !params.sprintId) return <Alert severity="info">Choose a sprint.</Alert>;
  if (result.key !== reqKey) return <Box sx={{ py: 6, textAlign: "center" }}><CircularProgress size={22} /></Box>;
  if (result.error) return <Alert severity="info">{result.error}</Alert>;
  const data = result.data;
  if (!data) return <Alert severity="info">No data.</Alert>;
  const withName = (rows, map) => rows.map((r) => ({ ...r, name: r.name || map?.get(String(r.id))?.name || "None" }));
  switch (name) {
    case "issues":
      return (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2.5 }}>
          <GlassCard sx={{ gridColumn: { md: "1 / -1" } }}>
            <CardHeader title="Created vs resolved" />
            <Box sx={{ height: 260, p: 2 }}>
              <ResponsiveContainer>
                <LineChart data={data.createdVsResolved}>
                  <CartesianGrid stroke={CHART.grid} vertical={false} />
                  <XAxis dataKey="date" tick={CHART.tick} {...CHART.axisProps} tickFormatter={(d) => d.slice(5)} />
                  <YAxis tick={CHART.tick} {...CHART.axisProps} width={30} allowDecimals={false} />
                  <RTooltip content={<ChartTooltip />} /><Legend />
                  <Line dataKey="created" name="Created" stroke={SERIES[0]} dot={false} strokeWidth={2} />
                  <Line dataKey="resolved" name="Resolved" stroke={SERIES[2]} dot={false} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </GlassCard>
          <GlassCard><CardHeader title="By type" /><Bars data={data.byType} x="name" bars={[["count", "Total"], ["open", "Open"]]} /></GlassCard>
          <GlassCard><CardHeader title="By priority" /><Bars data={data.byPriority} x="name" bars={[["count", "Total"], ["open", "Open"]]} /></GlassCard>
          <GlassCard sx={{ gridColumn: { md: "1 / -1" } }}><CardHeader title="By status" /><SimpleTable rows={data.byStatus} cols={[["name", "Status"], ["count", "Issues"], ["open", "Open"]]} /></GlassCard>
        </Box>
      );
    case "sprint":
      return (
        <Stack spacing={2.5}>
          <GlassCard sx={{ p: 2 }}>
            <Typography sx={{ fontWeight: 700 }}>{data.sprint.name}</Typography>
            <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary }}>Committed {data.committed?.points ?? 0} pts / {data.committed?.count ?? 0} issues · Completed {data.completed?.points ?? "—"} pts · Added after start {data.addedAfterStart?.count || 0} · Removed {data.removedAfterStart?.count || 0}</Typography>
          </GlassCard>
          <GlassCard><CardHeader title={`Completed (${data.issues.done.length})`} /><SimpleTable rows={data.issues.done} cols={[["key", "Key"], ["summary", "Summary"], ["storyPoints", "Pts"], ["assignee", "Assignee", (a) => a?.name || "—"]]} /></GlassCard>
          <GlassCard><CardHeader title={`Not completed (${data.issues.notDone.length})`} /><SimpleTable rows={data.issues.notDone} cols={[["key", "Key"], ["summary", "Summary"], ["storyPoints", "Pts"], ["assignee", "Assignee", (a) => a?.name || "—"]]} /></GlassCard>
        </Stack>
      );
    case "burndown":
      return <Burndown sprint={data.sprint} />;
    case "velocity":
      return project ? <Velocity project={project} /> : <SimpleTable rows={data.sprints} cols={[["name", "Sprint"], ["committed", "Committed"], ["completed", "Completed"], ["completionPct", "%"]]} />;
    case "release":
      return (
        <GlassCard>
          <SimpleTable rows={data.versions} cols={[["name", "Version"], ["status", "Status"], ["issues", "Issues"], ["done", "Done"], ["progressPct", "Progress", (v) => <Box sx={{ width: 120 }}><Progress value={v} /></Box>], ["openBugs", "Open bugs"], ["releaseDate", "Date", (d) => (d ? new Date(d).toLocaleDateString("en-IN") : "—")]]} />
        </GlassCard>
      );
    case "workload":
      return (
        <GlassCard>
          <Bars data={data.rows} x="name" layout="vertical" height={Math.max(220, data.rows.length * 34)} bars={[["open", "Open"], ["inProgress", "In progress"], ["overdue", "Overdue"]]} />
          <SimpleTable rows={data.rows} cols={[["name", "Person"], ["open", "Open"], ["inProgress", "In progress"], ["points", "Points"], ["remainingHours", "Remaining (h)"], ["overdue", "Overdue"]]} />
        </GlassCard>
      );
    case "time":
      return (
        <GlassCard>
          <CardHeader title={`${data.totalHours}h logged`} subtitle={`Grouped by ${data.groupBy}`} />
          <Bars data={withName(data.rows.slice(0, 15))} x="name" layout="vertical" height={Math.max(220, Math.min(15, data.rows.length) * 34)} bars={[["hours", "Hours"]]} />
          <SimpleTable rows={data.rows} cols={[["name", "Name"], ["hours", "Hours"], ["entries", "Entries"]]} />
        </GlassCard>
      );
    case "bugs":
      return (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2.5 }}>
          <GlassCard sx={{ p: 2 }}><Typography sx={labelSx}>Open bugs</Typography><Typography sx={{ fontSize: "1.6rem", fontWeight: 700 }}>{data.openCount}</Typography><Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary }}>Average open age: {data.averageOpenAgeDays ?? "—"} days</Typography></GlassCard>
          <GlassCard><CardHeader title="By priority" /><Bars data={data.byPriority} x="name" bars={[["count", "Total"], ["open", "Open"]]} height={200} /></GlassCard>
          <GlassCard sx={{ gridColumn: { md: "1 / -1" } }}><CardHeader title="Oldest open bugs" /><SimpleTable rows={data.open} cols={[["key", "Key"], ["summary", "Summary"], ["priorityId", "Priority", (id) => ctx.priorityById.get(String(id))?.name], ["assignee", "Assignee", (a) => a?.name || "—"], ["createdAt", "Opened", (d) => new Date(d).toLocaleDateString("en-IN")]]} /></GlassCard>
        </Box>
      );
    case "completion":
      return <GlassCard><CardHeader title="Completed work" subtitle={`Per ${data.interval}`} /><Bars data={data.rows} x="period" bars={[["issues", "Issues"], ["points", "Points"]]} /></GlassCard>;
    default:
      return null;
  }
};

const ProjectReports = ({ project }) => {
  const ctx = usePm();
  const [name, setName] = useState("issues");
  const [sprints, setSprints] = useState([]);
  const [params, setParams] = useState({ from: new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10), to: new Date().toISOString().slice(0, 10), interval: "week", groupBy: "user" });
  useEffect(() => {
    pm.sprints(project._id).then((s) => {
      setSprints(s);
      const pick = s.find((x) => x.status === "active") || s.find((x) => x.status === "completed");
      if (pick) setParams((p) => ({ ...p, sprintId: pick._id }));
    }).catch(() => {});
  }, [project._id]);
  const q = { ...params, projectId: project._id };
  const exportCsv = async () => {
    try {
      saveBlob(await pm.reportCsv(name, q), `${project.key}-${name}.csv`);
    } catch (err) {
      ctx.fail(err, "Export failed");
    }
  };
  return (
    <Box>
      <Tabs value={name} onChange={(_, v) => setName(v)} variant="scrollable" sx={{ mb: 2, minHeight: 36, "& .MuiTab-root": { minHeight: 36, textTransform: "none", fontSize: TYPE.small } }}>
        {REPORTS.filter(([k]) => project.sprintsEnabled || !["sprint", "burndown", "velocity"].includes(k)).map(([k, l]) => <Tab key={k} value={k} label={l} />)}
      </Tabs>
      <Stack direction="row" spacing={1.25} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap alignItems="center">
        {["issues", "time", "completion"].includes(name) && (
          <>
            <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={params.from} onChange={(e) => setParams({ ...params, from: e.target.value })} sx={fieldSx} />
            <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={params.to} onChange={(e) => setParams({ ...params, to: e.target.value })} sx={fieldSx} />
          </>
        )}
        {name === "completion" && <TextField select size="small" label="Interval" value={params.interval} onChange={(e) => setParams({ ...params, interval: e.target.value })} sx={{ ...fieldSx, minWidth: 110 }}>{["day", "week", "month"].map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}</TextField>}
        {name === "time" && <TextField select size="small" label="Group by" value={params.groupBy} onChange={(e) => setParams({ ...params, groupBy: e.target.value })} sx={{ ...fieldSx, minWidth: 120 }}>{["user", "issue", "sprint"].map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}</TextField>}
        {["sprint", "burndown"].includes(name) && (
          <TextField select size="small" label="Sprint" value={params.sprintId || ""} onChange={(e) => setParams({ ...params, sprintId: e.target.value })} sx={{ ...fieldSx, minWidth: 180 }}>
            {sprints.filter((s) => s.status !== "future").map((s) => <MenuItem key={s._id} value={s._id}>{s.name} ({s.status})</MenuItem>)}
          </TextField>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {has(project, "report.export") && <Button size="small" startIcon={<Download size={14} />} onClick={exportCsv}>Export CSV</Button>}
      </Stack>
      <ReportView name={name} params={q} project={project} />
    </Box>
  );
};

export default ProjectReports;
